import { FileSignature, Clock, Eye, CheckCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useContracts } from "@/hooks/useContracts";
import { useRealtimeContracts } from "@/hooks/useRealtimeContracts";
import { formatDistanceToNow } from "date-fns";

export function ContractPipelineWidget() {
  const { data: contracts, isLoading } = useContracts();
  
  // Enable real-time updates for contracts
  useRealtimeContracts();

  if (isLoading) {
    return (
      <Card className="glass">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const stats = {
    draft: contracts?.filter(c => c.status === 'draft').length || 0,
    sent: contracts?.filter(c => c.status === 'sent').length || 0,
    viewed: contracts?.filter(c => c.status === 'viewed').length || 0,
    signed: contracts?.filter(c => c.status === 'signed').length || 0,
  };

  const pendingContracts = contracts?.filter(c => 
    ['sent', 'viewed'].includes(c.status)
  ).slice(0, 3) || [];

  const totalValue = contracts?.reduce((sum, c) => {
    return sum + (c.deals?.amount || 0);
  }, 0) || 0;

  const signedValue = contracts?.filter(c => c.status === 'signed').reduce((sum, c) => {
    return sum + (c.deals?.amount || 0);
  }, 0) || 0;

  return (
    <Card className="glass">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-primary" />
          Contract Pipeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pipeline Progress */}
        <div className="flex items-center gap-1 h-2 rounded-full overflow-hidden bg-muted">
          {stats.draft > 0 && (
            <div 
              className="h-full bg-muted-foreground/50" 
              style={{ width: `${(stats.draft / (contracts?.length || 1)) * 100}%` }}
              title={`${stats.draft} drafts`}
            />
          )}
          {stats.sent > 0 && (
            <div 
              className="h-full bg-amber-500" 
              style={{ width: `${(stats.sent / (contracts?.length || 1)) * 100}%` }}
              title={`${stats.sent} sent`}
            />
          )}
          {stats.viewed > 0 && (
            <div 
              className="h-full bg-blue-500" 
              style={{ width: `${(stats.viewed / (contracts?.length || 1)) * 100}%` }}
              title={`${stats.viewed} viewed`}
            />
          )}
          {stats.signed > 0 && (
            <div 
              className="h-full bg-green-500" 
              style={{ width: `${(stats.signed / (contracts?.length || 1)) * 100}%` }}
              title={`${stats.signed} signed`}
            />
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="space-y-1">
            <div className="text-lg font-bold">{stats.draft}</div>
            <div className="text-xs text-muted-foreground">Draft</div>
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold text-amber-500">{stats.sent}</div>
            <div className="text-xs text-muted-foreground">Sent</div>
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold text-blue-500">{stats.viewed}</div>
            <div className="text-xs text-muted-foreground">Viewed</div>
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold text-green-500">{stats.signed}</div>
            <div className="text-xs text-muted-foreground">Signed</div>
          </div>
        </div>

        {/* Value Summary */}
        <div className="flex justify-between items-center py-2 border-t border-border/50">
          <span className="text-sm text-muted-foreground">Contract Value</span>
          <div className="text-right">
            <span className="text-sm font-semibold text-green-500">
              ${(signedValue / 1000).toFixed(0)}K
            </span>
            <span className="text-sm text-muted-foreground"> / </span>
            <span className="text-sm">${(totalValue / 1000).toFixed(0)}K</span>
          </div>
        </div>

        {/* Pending Signatures */}
        {pendingContracts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-3 w-3 text-amber-500" />
              Pending Signatures
            </h4>
            <div className="space-y-2">
              {pendingContracts.map((contract) => (
                <div 
                  key={contract.id} 
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {contract.status === 'viewed' ? (
                      <Eye className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                    ) : (
                      <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{contract.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {contract.signer_name || contract.signer_email || 'No signer'}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant={contract.status === 'viewed' ? 'default' : 'secondary'}
                    className="shrink-0 text-xs"
                  >
                    {contract.status === 'viewed' ? 'Viewed' : 'Sent'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {contracts?.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <FileSignature className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No contracts yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
